import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useI18nFormatting } from 'hooks/use-i18n-formatting';
import BrokerageLogos from './components/brokerage-logos';
import { SourceMlsBadge } from 'components/source-mls-badge';
import styles from './style.module.scss';
import { createProvidersComplianceApi } from 'data/providers-compliance';
import { getThemeByTenantId } from '@tenants';
import { CountryCodeList } from 'types/countries';
import LoadingSkeleton from 'components/loading-skeleton';
import { DEFAULT_SKELETON_HEIGHT } from 'components/footer/footer';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import { useTranslation } from 'next-i18next';
import { useGrowthBook } from '@growthbook/growthbook-react';
import { PROVIDER_DISCLAIMER_TRANSLATION_FLAG } from 'constants/feature-flags';

import type { PropsWithTenantId } from '@tenants/types';
import type { CourtesyOfListingData } from 'data/providers-compliance/providers.api';
import type Listing from 'data/listing';
import { DISCLAIMER_INFO_QUERY_KEY } from 'constants/use-query';

type ListingInfo = CourtesyOfListingData & Pick<Listing, 'url' | 'providerId' | 'countryCode' | 'isVancouverFeed' | 'id' | 'isCrea' | 'sourceMlsUrl'>;

type ProviderDisclaimerProps = PropsWithTenantId< {
  listingInfo: ListingInfo;
  /** When true, hides the courtesy text (useful when it's already shown in hero). This flag vaue not being put to its intended use and courtesy text is deduplicated for now */
  hideCourtesyText?: boolean;
}>;

export default function ProviderDisclaimer({ tenantId, listingInfo, hideCourtesyText = false }: ProviderDisclaimerProps) {
  const { t } = useTranslation(Namespaces.COMMON);
  const { formatDate } = useI18nFormatting();
  const growthbook = useGrowthBook();
  const isTranslationEnabled = growthbook?.getFeatureValue(PROVIDER_DISCLAIMER_TRANSLATION_FLAG, false);
  const tenantTheme = getThemeByTenantId(tenantId);
  const { providerId, id, brokerage, brokerageEmail, brokeragePhone, isVancouverFeed, lastFetched, isCrea, url, countryCode, sourceMlsUrl } = listingInfo;
  const isAmericanListing = listingInfo.countryCode === CountryCodeList.UNITED_STATES;

  const { data: disclaimer, isLoading } = useQuery({
    queryKey: [DISCLAIMER_INFO_QUERY_KEY, providerId, tenantTheme.id, id],
    queryFn: () => {
      const providerCompliance = createProvidersComplianceApi();
      const courtesyOfListingData: CourtesyOfListingData = {
        brokerage: listingInfo.brokerage,
        brokerageEmail: listingInfo.brokerageEmail,
        brokeragePhone: listingInfo.brokeragePhone,
        lastFetched: listingInfo.lastFetched,
        listingAgent: listingInfo.listingAgent,
        listingAgentContact: listingInfo.listingAgentContact,
        originalMlsName: listingInfo.originalMlsName,
        province: listingInfo.province,
      };
      return providerCompliance.getDisclaimer(providerId.toString(), tenantTheme.id, courtesyOfListingData);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  if (isLoading)
    return <LoadingSkeleton height={isAmericanListing ? DEFAULT_SKELETON_HEIGHT : '115px'} width={'100%'} className={styles.component}/>;

  if (!disclaimer || !disclaimer.Disclaimer)
    return null;

  // Changes made to not consider MLS_NUMBER_ONLY_PROVIDERS or courtesyProviderIds and to show courtesy text for all feeds.
  const showCourtesyOf = !hideCourtesyText && disclaimer?.CourtesyOf?.length > 0;

  return (
    <div className={styles.component}>
      {showCourtesyOf &&
        <p className={styles.courtesy}>
          {disclaimer.CourtesyOf /** FIXME: It does not support translation. This is returned by the API but there is no reason for that. we can create the same text in the frontend.*/}
        </p>
      }
      {!isAmericanListing && brokerage &&
        <p className={styles.courtesy}>
          {t(LOCALE_COMMON_PATHS.PROVIDER_DISCLAIMER_LISTED_BY,
            { brokerage,
              defaultValue: 'Listed by: {{brokerage}}',
            })
          }
        </p>
      }
      {isVancouverFeed &&
        <p className={styles.courtesy}>
          {brokerageEmail} {brokeragePhone}
        </p>
      }
      <BrokerageLogos providerId={providerId} countryCode={countryCode} creaUrl={isCrea ? url : undefined} />
      <div className={styles.disclaimer}>
        {!isAmericanListing && isTranslationEnabled ? (
          <div dangerouslySetInnerHTML={{ 
            __html: t(`provider_disclaimer_text.${providerId}`, { 
              COMPANY_NAME: tenantTheme.name,
              defaultValue: disclaimer.Disclaimer, 
            }), 
          }} />
        ) : (
          <div dangerouslySetInnerHTML={{ __html: disclaimer.Disclaimer }} />
        )}
      </div>
      {disclaimer.ManagingBroker &&
        <p className={styles.courtesy}>
          {disclaimer.ManagingBroker}
        </p>
      }
      {isAmericanListing && lastFetched &&
        <p>
          {t(LOCALE_COMMON_PATHS.PROVIDER_DISCLAIMER_DATA_LAST_UPDATED_AT,
            {
              date: formatDate(lastFetched),
              defaultValue: 'Data last updated at: {{date}}',
            })
          }
        </p>
      }
      {sourceMlsUrl &&
        <div className={styles['source-mls-badge-row']}>
          <SourceMlsBadge sourceMlsUrl={sourceMlsUrl} />
        </div>
      }
    </div>
  );
}
